/* eslint-disable no-unused-vars,prefer-rest-params */
/**
 * Setting an instance of axios to fetch the OF api. So from here, we can call
 * the api as follow:
 *   client.get('/members')
 *        .then(response => {
 *          console.log(response.data)
 *        })
 */
import {
  camel, query, snake,
} from 'kitsu-core';
import kebab from 'lodash.kebabcase';
import { singular } from 'pluralize';
import { saveAs } from 'file-saver';
import HasMany from '~/api/Abstracts/HasMany';
import HasOne from '~/api/Abstracts/HasOne';

class HttpClient { /* eslint-disable no-underscore-dangle */
  _instance = null;

  set(instance) {
    this._instance = instance;
  }

  get() {
    if (!this._instance) {
      throw new Error('Http client not initialized');
    }
    return this._instance;
  }
}
const client = new HttpClient();

export function init(axios) {
  client.set(axios);
}

const modelCollection = require
  .context('~/domains/', true, /^\.\/(?![^/]*Pivot)([^/]*)\/Model\.js$/);
const models = {};
modelCollection.keys().forEach((key) => {
  const ModelName = modelCollection(key).default.name;
  models[ModelName] = modelCollection(key).default;
});

const modelCollectionCollection = require
  .context('~/domains/', true, /^\.\/(?![^/]*Pivot)([^/]*)\/Collection\.js$/);
const modelCollections = {};
modelCollectionCollection.keys().forEach((key) => {
  const ModelCollectionName = modelCollectionCollection(key).default.name;
  modelCollections[ModelCollectionName] = modelCollectionCollection(key).default;
});

function preprocessNestedFilters(params) {
  if (params?.filter) {
    Object.keys(params.filter)
      .forEach((key) => {
        if (Array.isArray(params.filter[key])) {
          params.filter[key].forEach((filter, index) => {
            params.filter[`${key}][${index}`] = filter;
          });
          delete params.filter[key];
        }
      });
  }
}

function upperFirst(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function studlyCase(s) {
  return upperFirst(camel(s));
}

function getCollectionType(entities, guestedType = null) {
  const types = [...new Set(entities.map(({ type }) => kebab(type)))];
  const processedGuestedType = kebab(guestedType);

  if (types.length === 1 && (guestedType === null || processedGuestedType === types[0])) {
    return types[0];
  }

  if (types.length === 0 && guestedType != null) {
    return processedGuestedType;
  }

  throw new Error("Can't guess collection type");
}

function newCollection({
  entities,
  meta,
  type = null,
  parentType = undefined,
}) {
  try {
    const collectionType = getCollectionType(entities, type);
    const collectionName = `${studlyCase(collectionType)}Collection`;

    // eslint-disable-next-line no-use-before-define
    const instantiatedModels = entities.map((entity) => newEntity({
      entity,
      parentType,
    }));
    return new modelCollections[collectionName]({ meta }, ...instantiatedModels);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    // Return as simple array
    return entities;
  }
}

function newEntity({
  entity,
  parentType = undefined,
}) {
  if (!entity) return undefined;

  const { attributes } = entity;
  delete entity.attributes;
  const entityData = { ...entity, ...attributes };
  const Model = models[studlyCase(entity.type)];
  const relationshipTypes = Model.getJsonApiRelationshipTypes();

  if (entityData.meta && Object.keys(entityData.meta).length === 0) {
    delete entityData.meta;
  }

  if (entityData.relationships) {
    Object.keys(relationshipTypes).forEach((relationship) => {
      if (
        relationshipTypes[relationship] instanceof HasMany
        && Object.prototype.hasOwnProperty.call(entityData, 'relationships')
        && Object.prototype.hasOwnProperty.call(entityData.relationships, relationship)
      ) {
        entityData[relationship] = newCollection({
          entities: entity.relationships[relationship]?.data || [],
          meta: entity.relationships[relationship]?.meta,
          type: snake(relationshipTypes[relationship].entityName || singular(relationship)),
          parentType: entity.type,
        });
        delete entityData.relationships[relationship];
      }

      if (
        relationshipTypes[relationship] instanceof HasOne
        && Object.prototype.hasOwnProperty.call(entityData, 'relationships')
        && Object.prototype.hasOwnProperty.call(entityData.relationships, relationship)
      ) {
        entityData[relationship] = newEntity({
          entity: entity.relationships[relationship]?.data || entity.relationships[relationship],
          parentType: entity.type,
        });
        delete entityData.relationships[relationship];
      }

      if (
        Object.prototype.hasOwnProperty.call(entityData, 'relationships')
        && Object.keys(entityData.relationships).length === 0
      ) {
        delete entityData.relationships;
      }
    });
  }

  return new Model(entityData);
}

export async function $create(path, payload = {}, params = {}, config = {}) {
  preprocessNestedFilters(params);
  /* istanbul ignore next */
  const data = await client.get().post(`/v2/${path}`,
    {
      data: payload,
    }, {
      ...config,
      params,
      paramsSerializer: (p) => query(p),
    });

  return data;
}

export async function $patch(path, payload = {}, params = {}, config = {}) {
  preprocessNestedFilters(params);
  /* istanbul ignore next */
  const data = await client.get().patch(`/v2/${path}`,
    {
      data: {
        ...payload,
      },
    }, {
      ...config,
      params,
      paramsSerializer: (p) => query(p),
    });

  return data;
}

export async function $delete(path, payload, params = {}, config = {}) {
  preprocessNestedFilters(params);

  let uri = `/v2/${path}`;
  if (payload) {
    uri += `/${payload}`;
  }
  /* istanbul ignore next */
  await client.get().delete(uri,
    {
      ...config,
      params,
      paramsSerializer: (p) => query(p),
    });
}

export async function $post(url, data = {}, config = {}) {
  return (await client.get().post.apply(undefined, arguments)).data;
}

export async function $put(url, data = {}, config = {}) {
  return (await client.get().put.apply(undefined, arguments)).data;
}

export async function $upload(path, formData, params = {}, config = {}) {
  preprocessNestedFilters(params);
  /* istanbul ignore next */
  await client.get().post(`/v2/${path}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params,
    timeout: 0,
    ...config,
    paramsSerializer: (p) => query(p),
  });
}

export async function $download(url, data = {}, config = {}, method = 'get') {
  const response = await client.get()({
    url,
    method,
    responseType: 'blob', // important
  });
  saveAs(new Blob([response.data]), config.baseName);
}

export async function $get(path, data = {}, config = {}, method = 'get') {
  const response = await client.get()({
    url: `/v2/${path}`,
    method,
  });
  return response;
}

export default client;
